/**
 * 서류/사진 업로드·편집 시트 (BS 타겟)
 *
 * 호출:
 *   $j.Ui.BS.open({
 *       target: '$j.F.Docs.v1.UploadSheet',
 *       data: {
 *           tar,              // 클릭된 박스 DOM (data_rsno 갱신용)
 *           docName,          // 서류명 전체
 *           hasDate,          // 0/1
 *           dataCode,         // doc_code (5,12,1,2,9999,...)
 *           maxPages,         // 종류의 총 장수 상한 (g5_document_types.max_pages). 없으면 기본 10
 *           remainPages,      // 더 올릴 수 있는 장수 (서버 계산 — DocService::remainByCode)
 *           oldDate,          // 기존 유효기간
 *           subCode, tarMaxnum, showImg, showNum, intype, sstype, seltyett,
 *           existingData,     // 기존 업로드된 서류 배열
 *           editmembernum,
 *           carnum,
 *           userGroups,       // 커스텀 서류명 배열 (중복체크)
 *       }
 *   });
 *
 * 이벤트 발행 (EventBus):
 *   docs:changed       업로드·삭제 성공 후 { dataCode, data, dbtype }
 *   docs:new-custom    커스텀 서류(9999) 최초 업로드 후 { dataCode, docName, intype, data }
 */
import Util from '../../../Common/Util';

export default class UploadSheet {
    // 은행 목록 — 통장사본 OCR 이 읽는 목록(OcrService::$bankList)에 통장이 나오는 나머지 기관을 더했다.
    // 이름에 공백을 넣지 않는다: 계좌값은 "{은행} {계좌} {예금주}" 공백연결이라 은행에 공백이 있으면 파싱이 깨진다.
    static BANKS = [
        '국민은행', '신한은행', '우리은행', '하나은행', '농협은행', '기업은행',
        '카카오뱅크', '토스뱅크', '케이뱅크',
        '새마을금고', '신협', '우체국', '수협',
        'SC제일은행', '씨티은행', '산업은행',
        '부산은행', 'iM뱅크', '경남은행', '광주은행', '전북은행', '제주은행',
    ];
    // 목록에 없는 은행을 직접 입력받는 선택지 (값이 겹치지 않게 은행명이 아닌 토큰을 쓴다)
    static BANK_ETC = '__etc';
    // 펼친 목록의 최대 높이(px) — 6칸쯤. 화면 바닥까지 남은 자리가 이보다 좁으면 그쪽에 맞춘다.
    static PICKER_MAX_H = 264;
    // 계좌번호 자릿수 상한 — 국내 은행 최장이 14자리라 여유를 뒀다.
    static ACCOUNT_MAX_LEN = 20;

    // 하단시트로 받는 입력칸 선언 — 계좌(12)·사업자정보(5) 가 같은 시트를 쓴다.
    // 부모 시트에는 대표값 한 줄(계좌번호 / 사업자번호)만 두고, 실제 입력은 여기 칸으로 받는다.
    // key 는 그대로 저장 파라미터명이다(사업자정보 = g5_company 컬럼).
    static FIELD_SHEETS = {
        bank: {
            title: '계좌 정보 입력',
            fields: [
                { key: 'bank',    label: '은행',     ph: '은행 선택',       type: 'select', options: UploadSheet.BANKS, etcPh: '은행명 직접 입력' },
                { key: 'account', label: '계좌번호', ph: '예: 123456789', format: 'digits', attrs: 'inputmode="numeric"' },
                { key: 'holder',  label: '예금주',   ph: '예: 홍길동' },
            ],
        },
        biz: {
            title: '사업자 정보 입력',
            fields: [
                { key: 'co_number',  label: '사업자번호',     ph: '사업자번호 (10자리 숫자)', format: 'bizno', attrs: 'inputmode="numeric" maxlength="12"' },
                { key: 'co_title',   label: '상호',           ph: '사업자등록증의 상호',     attrs: 'maxlength="50"' },
                { key: 'co_ceoname', label: '대표이름',       ph: '대표자 성명',             attrs: 'maxlength="50"' },
                { key: 'co_birth',   label: '대표생일',       ph: 'YYYY-MM-DD',              format: 'date',  attrs: 'inputmode="numeric" maxlength="10"' },
            ],
        },
    };

    /**
     * 상한·남은 장수를 모르는 경우의 기본값 (마스터 `etc`·`etc_car` 와 같은 값).
     *
     * 값 자체는 여기 있지 않다 — `g5_document_types.max_pages` 가 정본이고, 남은 장수는 서버가
     * 세서 `remainPages`·`maxPages` 로 내려준다. **예전엔 이 파일의 상수표였고**, 관리자가
     * 서류마스터에서 고쳐도 배포된 상수가 계속 이겨서 값이 두 곳으로 갈렸다.
     *
     * 남은 장수를 시트가 직접 셀 수 없다 — `lastShowData` 는 공통서류 화면에서 소속 기사 전원의
     * 장을 모아 놓은 것이라 "이 서류의 장 수"가 아니다 (DocService::remainByCode).
     */
    static MAX_PAGES_DEFAULT = 10;

    /** 이 서류가 가질 수 있는 **총** 장수 (마스터 값, 모르면 기본값) */
    _maxPages() {
        return this.maxPages || UploadSheet.MAX_PAGES_DEFAULT;
    }

    /**
     * **이번에** 고를 수 있는 장수. 서버가 준 값을 등록할 때마다 깎는다.
     * 서버가 셀 수 없다고 한 칸(값이 없음)은 상한을 그대로 쓴다 — 오늘과 같은 성격이고 상한만 맞는다.
     */
    _remainPages() {
        return this.remainPages == null ? this._maxPages() : Math.max(0, this.remainPages);
    }

    /**
     * 더 올릴 자리가 없으면 알리고 true. **업로드 흐름의 첫 관문이다** — 뒤에 입력 단계가 있어서
     * 나중에 막으면 회원이 칸을 다 채우고 나서 거절당한다.
     * 교체(update)는 장수가 늘지 않으므로 통과시킨다.
     */
    _blockedByPageLimit() {
        if (this.uploadMode === 'update') return false;
        if (this._remainPages() > 0) return false;
        Util.swalert(`${this.docName || '이 서류'}는 최대 ${this._maxPages()}장까지 등록할 수 있습니다.\n`
              + '사진을 더 올리려면 기존 사진을 먼저 삭제해 주세요.');
        return true;
    }

    constructor() {
        this._resetState();
    }

    _resetState() {
        this.docName       = '';
        this.hasDate       = 0;
        this.hasDateVal    = '';
        this.dataCode      = 0;
        this.dataSubCode   = 1;
        this.tarMaxnum     = 1;
        this.oldDate       = '';
        this.showNum       = 0;
        this.uploadMode    = 'self';
        this.editOrderNth  = 10;
        this.editIntype    = 0;
        this.userTitle     = '';
        this.lastShowData  = [];
        this.maxPages      = 0;
        this.remainPages   = null;
        this.firstDataHas  = 0;
        this.targetObj     = null;
        this.userGroups    = [];
        this.editmembernum = 0;
        this.carnum        = 0;
    }

    destroy() {
        $(`#${this.id}`).off();
        $j.Common.EventBus.offNamespace($j.getNS(this));
    }

    init() {
        const d = this.data || {};

        this.targetObj     = d.tar || null;
        this.editIntype    = d.intype || 0;
        this.dataSubCode   = d.subCode || 1;
        this.docName       = d.docName || '';
        this.hasDate       = d.hasDate || 0;
        this.dataCode      = d.dataCode;
        this.oldDate       = d.oldDate || '';
        this.tarMaxnum     = d.tarMaxnum || 1;
        this.maxPages      = parseInt(d.maxPages) || 0;
        this.remainPages   = (d.remainPages == null) ? null : (parseInt(d.remainPages) || 0);
        this.editmembernum = d.editmembernum || 0;
        this.carnum        = d.carnum || 0;
        this.userGroups    = Array.isArray(d.userGroups) ? d.userGroups.slice() : [];
        this.userTitle     = '';
        this.uploadMode    = 'self';
        this.editOrderNth  = 10;
        this.allowSpecialLabel = d.seltyett === 'selthsaseditnum';

        // 사업자번호는 g5_company 값이 정본이지만, 아직 거기 저장되기 전이면
        // 서류행에 실린 번호(oldDate)가 우리가 아는 유일한 값이다 — 시트가 그 값을 이어받는다.
        if (this._hasBizPanel() && !d.coNumber && this._isValidBizNo(this.oldDate)) {
            d.coNumber = this.oldDate;
        }

        let showNum = d.showNum;

        // BS 헤더 타이틀 — 실제 서류명 노출 (9999=커스텀 추가 / dataCode 없으면 fallback)
        const bsTitle = this.docName
            || (this.dataCode == 9999 ? '서류 추가등록' : '서류 업로드');
        this.parent?.setTitle?.(bsTitle);

        this.draw();
        this.initHandler();
        this._initUploadListener();

        // dataCode 별 입력 모드 (라벨 + datepicker 바인딩)
        //   seltyett 는 레거시에서 "부모페이지에서만 특수라벨 적용" 용도였으나,
        //   실제로는 hasDate=1 인 모든 경우에 datepicker 가 필요하므로 무조건 호출.
        this._setInputMode(this.dataCode, d.seltyett === 'selthsaseditnum');

        // data_rsno 속성에서 showNum 보정 (클릭한 박스 기준)
        if (showNum != 99999) {
            const rsno = this.targetObj ? parseInt(this.targetObj.getAttribute('data_rsno')) : NaN;
            showNum = isNaN(rsno) ? -1 : rsno;
            $(`#${this.id}`).find('.js-big-row1').hide();
            $(`#${this.id}`).find('.js-big-tit-val1').attr('type', 'hidden');
        } else {
            $(`#${this.id}`).find('.js-big-row1').show();
            $(`#${this.id}`).find('.js-big-tit-val1').attr('type', 'text');
            this.editOrderNth = 1;
            this.dataSubCode  = this.editIntype;
        }
        this.showNum = showNum;

        // 단순 업로드 경로 — 기존자료 없고 입력 불필요시 바로 파일 다이얼로그
        //   사업자정보(상호·대표이름·대표생일)가 비어 있으면 이 경로로 빠지지 않는다. 사진만 찍고 나가면
        //   받을 기회가 없는 값이라, 등록 전에 한 번은 칸을 보여준다.
        if (showNum == -1 || (d.showImg == '' && showNum == 0)) {
            if (!this._requiresInput() && !this._bizNeedsInput() && this.docName.length > 1) {
                this.uploadMode = 'self';
                this._doUpload();
                return;
            }
        }

        // 기존값 반영
        $(`#${this.id}`).find('.js-big-tit-val1').val(this.docName);
        $(`#${this.id}`).find('.js-big-tit-val2').val(this.oldDate);

        this.firstDataHas = this.docName.length;
        if (this.docName.length > 0) {
            this.lastShowData = Array.isArray(d.existingData) ? d.existingData : [];
            this._renderBigImgOnlist(this.lastShowData);
            $(`#${this.id}`).find('.js-big-tit-val1').prop('readonly', true);
        } else {
            $(`#${this.id}`).find('.js-big-img-onlist').html('');
            $(`#${this.id}`).find('.js-big-tit-val1').prop('readonly', false);
        }

        // 입력 row 자체를 wrapper 단위로 토글 (label+input 통째)
        // 사업자등록증은 사업자번호가 이미 잠겼어도 칸을 보여준다 — 아래 상호·대표이름과 한 판으로 읽힌다
        if (!this._requiresInput() && !this._hasBizPanel()) {
            $(`#${this.id}`).find('.js-big-row2').hide();
        } else {
            $(`#${this.id}`).find('.js-big-row2').show();
        }

        this._hideAddMoreButton();
    }

    draw() {
        // 라벨/입력칸은 각각 .js-big-tit* / .js-big-tit-val* 로 잡는다 (init·_setInputMode 가 참조).
        // .js-big-tit1(서류명)은 커스텀 추가(9999)일 때만, .js-big-row2 는 입력이 필요할 때만 보인다.
        // .js-biz-rows 는 사업자등록증(5) 의 안내줄 — _renderBizNote() 가 채운다.
        // (상호·대표이름·대표생일 입력칸 자체는 하단시트로 옮겼다 — _openFieldSheet('biz'))
        //
        // 시트 맨 위 안내문구(js-popimgtext)는 걷었다 — '유효기간 입력'·'계좌번호 입력' 처럼
        // 바로 아래 라벨을 한 번 더 말하는 줄이었다. 무엇을 넣는 칸인지는 placeholder 가 말한다.
        const html = `
        <div class="doc-sheet">
            <div class="doc-sheet-row js-big-row1" style="display:none">
                <div class="doc-sheet-lb js-big-tit1">서류명</div>
                <input type="hidden" class="doc-sheet-inp js-big-tit-val1" placeholder="서류명을 직접 입력">
            </div>
            <div class="doc-sheet-row js-big-row2" style="display:none">
                <div class="doc-sheet-lb js-big-tit2">유효기간</div>
                <input type="text" class="doc-sheet-inp js-big-tit-val2" placeholder="유효기간 입력 (YYYY-MM-DD)" inputmode="numeric" maxlength="10">
            </div>
            <div class="js-biz-rows" style="display:none"></div>
            <div class="js-big-img-onlist"></div>
            <button type="button" class="doc-tile is-add doc-sheet-add js-add-more" style="display:none">
                <span class="doc-tile-thumb"><i class="fas fa-plus doc-tile-ico"></i></span>
                <span class="doc-tile-name">서류추가등록</span>
            </button>
        </div>`;
        $(`#${this.id}`).html(html);
    }

    initHandler() {
        const self = this;
        const $el = $(`#${this.id}`);

        $el.on('click', '.js-add-more', () => self.onClickAddMore());

        $el.on('click', '.js-edit-photo', function () {
            const num   = parseInt($(this).data('num'));
            const imgId = $(this).data('img-id');
            self.onClickEditPhoto(num, imgId);
        });
        $el.on('click', '.js-change-photo', function () {
            const num = parseInt($(this).data('num'));
            const idx = parseInt($(this).data('idx'));
            self.onClickOldDataSet(1, num, idx);
        });
        $el.on('click', '.js-delete-photo', function () {
            const num = parseInt($(this).data('num'));
            const idx = parseInt($(this).data('idx'));
            self.onClickOldDataSet(2, num, idx);
        });
        $el.on('click', '.js-zoom-img', function () {
            const num = parseInt($(this).data('rs-num'));
            const items = (self.lastShowData || [])
                .filter(d => d && d[1])
                .map(d => ({ url: d[1], rsNum: d[7] }));
            const index = Math.max(0, items.findIndex(it => it.rsNum === num));
            if (items.length === 0) {
                $j.Ui.ZoomPhoto.open(this.src);
                return;
            }
            $j.Ui.ZoomPhoto.open(null, { items, index });
        });
    }

    _initUploadListener() {
        const self = this;
        const ns = $j.getNS(this);
        $j.Common.EventBus.on(ns, 'upload:image', function (e, payload) {
            if ($j.F.Upload.getContext() !== 'stampupload') return;
            const files = (Array.isArray(payload) ? payload : [payload]).filter(f => f && f.id);
            if (!files.length) {
                console.error('[UploadSheet] file.id missing in payload', payload);
                Util.swalert('업로드 응답 오류 (file.id 누락)');
                return;
            }
            self._commitFiles(files);
        });

        // 이미지 편집 저장 후 내부 프리뷰만 갱신 (BS 유지)
        $j.Common.EventBus.on(ns, 'docs:image-edited', function (e, payload) {
            if (!payload || payload.dataCode != self.dataCode) return;
            // lastShowData 의 해당 행(num=docId) 갱신
            if (Array.isArray(self.lastShowData)) {
                self.lastShowData.forEach(row => {
                    if (row && row[7] == payload.docId) {
                        row[1]  = payload.imageUrl;
                        row[2]  = payload.thumbUrl;
                        row[10] = payload.imgRelId;
                    }
                });
            }
            // 미리보기 img src 캐시버스트
            const $img = $(`#${self.id}`).find(`img[data-rs-num="${payload.docId}"]`);
            if ($img.length) $img.attr('src', payload.imageUrl + '?stmp=' + Date.now());
        });
    }

    // dataCode 에 맞게 입력 필드 모드 전환
    //   allowSpecialLabel: true 면 5/12 코드에 대해 "사업자번호/계좌번호" 라벨 적용 (부모페이지 전용)
    //   이 두 코드의 칸은 직접 타이핑하지 않는다 — 눌러서 하단시트를 연다(_openFieldSheet).
    _setInputMode(dataCode, allowSpecialLabel) {
        const $el   = $(`#${this.id}`);
        const $bt2  = $el.find('.js-big-tit2');
        const $val2 = $el.find('.js-big-tit-val2');
        // 모드 전환 시 이전 핸들러 정리
        $val2.off('click.fsheet input.date change.date');

        if (allowSpecialLabel && dataCode == 5) {
            $bt2.html('사업자번호');
            // 잠금 표현은 [readonly] 스타일(pages/_docs-v1.scss)이 담당한다 — 인라인 hex 는 다크에서 못 덮는다.
            $val2.attr('readonly', true).removeAttr('inputmode').removeAttr('maxlength');
            if (this._bizAllLocked()) {
                // 네 칸 모두 g5_company 에 등록됨 — 표시만, 수정 불가
                $val2.attr('placeholder', '').removeClass('is-tappable');
            } else {
                $val2.attr('placeholder', '사업자번호 (10자리 숫자)').addClass('is-tappable')
                    .on('click.fsheet', () => this._openFieldSheet('biz'));
            }
            this._renderBizNote();
            return;
        }
        if (allowSpecialLabel && dataCode == 12) {
            $bt2.html('계좌번호');
            $val2.attr('readonly', true);
            if (this.data?.bankLocked) {
                // 관리자(g5_company.co_bank1) 가 설정된 경우 — 읽기 전용, 수정 불가
                $val2.attr('placeholder', '').removeClass('is-tappable');
            } else {
                // 미설정 — readonly 지만 눌러서 3필드 시트를 여는 칸이라 '눌러라'로 보여야 한다
                $val2.attr('placeholder', '은행 · 계좌 · 예금주').addClass('is-tappable')
                    .on('click.fsheet', () => this._openFieldSheet('bank'));
            }
            return;
        }
        if (dataCode == 1) {
            $bt2.html('차량검사만료일');
        } else if (dataCode == 2) {
            $bt2.html('보험만료일');
        } else {
            $bt2.html('유효기간');
        }
        $val2.attr('placeholder', '유효기간 입력 (YYYY-MM-DD)').removeAttr('readonly').removeClass('is-tappable').attr('inputmode', 'numeric');
        $val2.on('input.date', (e) => this._formatDateInput(e.target));
        $val2.on('change.date', (e) => {
            const v = e.target.value;
            if (v && !this._isValidDateStr(v)) {
                $j.Ui.Toast.show('warning', '날짜 형식이 올바르지 않습니다. (YYYY-MM-DD)');
                return;
            }
            this._checkExistsUpdate(v);
        });
    }

    // 숫자만 남기고 YYYY-MM-DD 자동 포맷 (최대 8자리)
    _formatDateInput(el) {
        const d = (el.value || '').replace(/\D/g, '').slice(0, 8);
        let out = d;
        if (d.length > 6)      out = `${d.slice(0,4)}-${d.slice(4,6)}-${d.slice(6)}`;
        else if (d.length > 4) out = `${d.slice(0,4)}-${d.slice(4)}`;
        el.value = out;
    }

    // YYYY-MM-DD 형식 + 실존 날짜 검증 (round-trip 으로 2024-02-30 같은 케이스 거름)
    _isValidDateStr(s) {
        if (!/^\d{4}-\d{2}-\d{2}$/.test(s)) return false;
        const d = dayjs(s);
        return d.isValid() && d.format('YYYY-MM-DD') === s;
    }

    // 사진 업로드 전 입력해야 하는 항목이 있는지
    //  - hasDate==1 : 유효기간 입력 필요
    //  - 5/12 (allowSpecialLabel) : 사업자번호/계좌번호 입력 필요 (잠금 상태가 아닐 때만)
    _requiresInput() {
        if (this.hasDate == 1) return true;
        if (this.allowSpecialLabel) {
            if (this.dataCode == 5 && !this.data?.coNumberLocked) return true;
            if (this.dataCode == 12 && !this.data?.bankLocked) return true;
        }
        return false;
    }

    // 입력칸이 잠금(readonly)인지 — 사업자번호/계좌번호가 이미 등록된 경우.
    // 유저가 고칠 수 없는 값이므로 검증·입력요구 대상에서 제외한다.
    _isValueLocked() {
        if (!this.allowSpecialLabel) return false;
        if (this.dataCode == 5)  return !!this.data?.coNumberLocked;
        if (this.dataCode == 12) return !!this.data?.bankLocked;
        return false;
    }

    // 숫자만 남기고 XXX-XX-XXXXX 자동 포맷 (최대 10자리)
    _formatBizNoInput(el) {
        const d = (el.value || '').replace(/\D/g, '').slice(0, 10);
        let out = d;
        if (d.length > 5)      out = `${d.slice(0,3)}-${d.slice(3,5)}-${d.slice(5)}`;
        else if (d.length > 3) out = `${d.slice(0,3)}-${d.slice(3)}`;
        el.value = out;
    }

    // 숫자만 남긴다 (계좌번호) — 공백·하이픈·문자를 입력·붙여넣기 즉시 걷어낸다.
    //   계좌번호는 은행마다 자릿수·구분위치가 달라 자동 하이픈을 넣을 수 없다. 그래서 숫자만 받는다.
    //   자릿수 상한은 여기서 건다 — input[maxlength] 로 걸면 '걷어내기 전' 원문 길이로 잘려서
    //   하이픈 섞인 계좌를 붙여넣을 때 뒷자리가 날아간다.
    //   커서는 '앞쪽에서 살아남은 개수' 로 잡는다 — 중간을 고칠 때 커서가 끝으로 튀지 않게.
    _stripNonDigits(el) {
        const raw = el.value || '';
        const out = raw.replace(/\D/g, '').slice(0, UploadSheet.ACCOUNT_MAX_LEN);
        if (out === raw) return;
        const pos  = el.selectionStart || 0;
        const kept = Math.min(raw.slice(0, pos).replace(/\D/g, '').length, out.length);
        el.value = out;
        try { el.setSelectionRange(kept, kept); } catch (_) {}
    }

    // 사업자번호 검증 — 서버(DocService@upload)와 동일하게 "숫자 10자리" 기준.
    // 하이픈 유무는 따지지 않는다 (g5_company.co_number 레거시값 다수가 하이픈 없는 10자리).
    _isValidBizNo(s) {
        return String(s || '').replace(/\D/g, '').length == 10;
    }

    // ── 사업자정보 (doc_code=5 전용) ────────────────────────────
    //  사업자번호·상호·대표이름·대표생일은 관리자 기사관리 '사업자정보' 와 **같은 g5_company 칸**이다.
    //  저장소가 하나라 어느 쪽에서 넣어도 반대쪽에 그대로 보인다 — 그게 동기화의 실체다.
    //  네 칸 모두 **값이 차는 순간 잠긴다**(readonly). CS 가 서류로 확인해 확정하는 값이라
    //  회원 입력이 덮으면 안 된다 — 수정은 관리자 화면에서 한다. 서버(saveCompanyBiz)도 같은 규칙이다.

    // 이 시트가 사업자정보 칸을 그리는가 (부모페이지의 사업자등록증 타일에서만)
    _hasBizPanel() {
        return !!this.allowSpecialLabel && this.dataCode == 5;
    }

    // 아직 비어 있는 사업자정보 칸이 있는가 — 있으면 사진 다이얼로그로 직행하지 않고 시트를 보여준다
    _bizNeedsInput() {
        if (!this._hasBizPanel()) return false;
        const d = this.data || {};
        return !d.coTitle || !d.coCeoname || !d.coBirth
            || !(d.coNumberLocked || this._isValidBizNo(d.coNumber));
    }

    // 부모 시트에 남은 한 줄(사업자번호) 아래 안내 — 잠긴 칸이 하나라도 있을 때만 나온다.
    // 비어 있는 항목을 여기서 다시 나열하지 않는다 (시트를 열면 빈 칸이 그대로 보인다).
    _renderBizNote() {
        if (!this._hasBizPanel()) return;
        const note = Object.values(this._bizLocks()).some(Boolean)
            ? '<div class="doc-sheet-hint">등록된 항목은 수정할 수 없습니다. 변경은 고객센터로 문의해 주세요.</div>'
            : '';
        $(`#${this.id}`).find('.js-biz-rows').html(note).show();
    }

    // 사업자정보 4칸 값. 칸은 하단시트에 있고 시트는 닫히면 사라지므로 값은 this.data 가 들고 있는다.
    // 사업자번호는 10자리로 완성됐을 때만 싣는다 — 반쪽짜리 값이 나머지 칸 저장까지 막으면 안 된다.
    // 정보는 사진 없이 먼저 저장될 수 있지만, 사진은 네 칸이 다 차야 올라간다(_doUpload).
    _bizValues() {
        const out = { co_number: '', co_title: '', co_ceoname: '', co_birth: '' };
        if (!this._hasBizPanel()) return out;
        const d = this.data || {};

        const no = String(d.coNumber || '').trim();
        if (this._isValidBizNo(no)) out.co_number = no;
        out.co_title   = String(d.coTitle   || '').trim();
        out.co_ceoname = String(d.coCeoname || '').trim();
        out.co_birth   = String(d.coBirth   || '').trim();
        return out;
    }

    // 칸별 잠금 — 값이 차는 순간 잠긴다(CS 가 서류로 확인해 확정하는 값이라 회원 입력이 덮으면 안 된다)
    _bizLocks() {
        const d = this.data || {};
        return {
            co_number:  !!d.coNumberLocked,
            co_title:   !!d.coTitle,
            co_ceoname: !!d.coCeoname,
            co_birth:   !!d.coBirth,
        };
    }

    // 네 칸이 모두 잠겼는가 — 그러면 부모 시트의 칸을 눌러도 열 것이 없다
    _bizAllLocked() {
        const l = this._bizLocks();
        return l.co_number && l.co_title && l.co_ceoname && l.co_birth;
    }

    // g5_company 저장 (서버는 빈값을 무시한다 — 안 채운 칸이 기존 값을 지우지 않는다)
    _saveCompanyBiz() {
        const vals = this._bizValues();

        $.ajax({
            type: 'POST', url: '/m/ajax/docs/company', dataType: 'json', data: vals,
            success: (r) => {
                if (!r || !r.result) {
                    $j.Ui.Toast.show('warning', (r && r.error) || '사업자정보 저장 실패');
                    return;
                }
                // 서버가 정규화한 값으로 시트 상태를 맞춘다 (BS 닫히면 부모가 refresh 로 다시 받는다)
                const c = r.company || {};
                this.data.coTitle   = c.co_title   || '';
                this.data.coCeoname = c.co_ceoname || '';
                this.data.coBirth   = c.co_birth   || '';
                this._applyBizLocks(c);
                $j.Ui.Toast.show('success', '사업자정보 저장됨');
            },
            error: () => $j.Ui.Toast.show('warning', '사업자정보 저장 실패')
        });
    }

    // 서버가 확정한 값으로 상태·부모 시트 한 줄을 맞추고, 잠금 여부에 따라 칸을 다시 세운다.
    // 이걸 안 하면 "고쳐서 저장했는데 안 바뀐다"가 생긴다 — 서버가 등록된 값을 다시 받지 않으므로.
    _applyBizLocks(c) {
        if (c.co_number) {
            this.data.coNumber       = c.co_number;
            this.data.coNumberLocked = true;
            $(`#${this.id}`).find('.js-big-tit-val2').val(c.co_number);
        }
        // 잠금 상태가 바뀌었으니 칸(탭 가능 여부)·안내줄을 다시 세운다
        this._setInputMode(this.dataCode, this.allowSpecialLabel);
    }

    // 계좌·사업자정보 입력 하단시트 (공용 엔진 $j.Ui.Modal 의 sheet 모드)
    //   modal 슬롯(1055)이 BS(1035)보다 위라 업로드 시트 위로 아래에서 올라온다.
    //   칸 선언은 FIELD_SHEETS 하나뿐이고 이 아래는 전부 범용 로직이다.
    //   onDone 을 넘기면 확인이 성공하고 **사업자정보 네 칸이 다 찼을 때만** 시트를 닫고 부른다
    //   (사업자등록증 사진 전 입력 — _doUpload). 안 넘기면 예전과 같다.
    _openFieldSheet(kind, onDone) {
        const self = this;
        const spec  = UploadSheet.FIELD_SHEETS[kind];
        if (!spec) return;
        const vals  = kind === 'bank' ? this._bankValues() : this._bizValues();
        const locks = kind === 'bank' ? {} : this._bizLocks();

        const rows = spec.fields.map((f) => `
            <div class="doc-fsheet-fld">
                <label>${f.label}</label>
                ${self._fieldControl(f, vals[f.key] || '', !!locks[f.key])}
            </div>`).join('');

        const note = Object.values(locks).some(Boolean)
            ? '<div class="doc-fsheet-hint">등록된 항목은 수정할 수 없습니다. 변경은 고객센터로 문의해 주세요.</div>'
            : '';

        // dismissible:false — 입력 중 백드롭을 스쳐 눌러 폼이 날아가지 않게. 취소/확인/✕ 로만 닫는다.
        const handle = $j.Ui.Modal.open({
            title: spec.title,
            mode: 'sheet',
            variant: 'docs',
            dismissible: false,
            onClose: () => self._closePicker(),   // 시트를 닫으면 펼친 목록도 같이 걷는다
            html: `
            <div class="doc-fsheet">
                ${rows}
                ${note}
                <div class="doc-fsheet-btns">
                    <button type="button" class="doc-fsheet-cancel js-fs-cancel">취소</button>
                    <button type="button" class="doc-fsheet-ok js-fs-ok">확인</button>
                </div>
            </div>`,
        });

        const $c = handle.el;

        // 입력 포맷 — 선언에 적힌 것만 건다
        spec.fields.forEach((f) => {
            const $inp = $c.find(`.js-fs-inp[data-key="${f.key}"]`);
            if (f.format === 'bizno') $inp.on('input', (e) => self._formatBizNoInput(e.target));
            if (f.format === 'date')  $inp.on('input', (e) => self._formatDateInput(e.target));
            if (f.format === 'digits') $inp.on('input', (e) => self._stripNonDigits(e.target));
        });

        // 목록형 칸 — 네이티브 select 대신 칸 아래로 목록을 펼친다 (OS 가 그리는 목록은 꾸밀 수 없다)
        $c.on('click', '.js-fs-pick', function () {
            // 열려 있는 칸을 다시 누르면 닫는다
            if ($(this).hasClass('is-open')) { self._closePicker(); return; }
            const f = spec.fields.find(x => x.key === $(this).data('key'));
            if (f) self._openPicker(f, $(this));
        });

        $c.on('click', '.js-fs-cancel', () => handle.close());
        $c.on('click', '.js-fs-ok', () => {
            const out = {};
            $c.find('.js-fs-inp').each(function () {
                out[$(this).data('key')] = ($(this).val() || '').trim();
            });
            $c.find('.js-fs-pick').each(function () {
                const val = String($(this).attr('data-val') || '');
                out[$(this).data('key')] = val === UploadSheet.BANK_ETC
                    ? ($(this).siblings('.js-fs-etc').val() || '').trim()
                    : val;
            });
            if (!self._applyFieldSheet(kind, out)) return;   // 검증 실패면 시트를 열어둔다
            if (onDone && self._bizNeedsInput()) {
                Util.swalert('사업자정보를 모두 입력해야 사진을 등록할 수 있습니다.');
                return;
            }
            handle.close();
            if (onDone) onDone();
        });

        // 첫 칸 자동 포커스 — 단 목록형이면 걸지 않는다 (누르면 시트가 뜨는 칸이라 키보드가 방해만 된다)
        const $first = $c.find('.js-fs-pick, .js-fs-inp:not([readonly])').first();
        if ($first.is('input')) setTimeout(() => $first.trigger('focus'), 80);
    }

    // 칸 하나의 컨트롤 HTML. 선언(FIELD_SHEETS)에 type:'select' 가 있으면 목록 + '기타' 직접입력칸.
    //   목록에 없는 기존값(레거시·다른 표기)은 자동으로 '기타' 쪽으로 들어간다 — 값을 잃지 않는다.
    _fieldControl(f, v, lock) {
        if (f.type !== 'select') {
            return `<input type="text" class="js-fs-inp" data-key="${f.key}"
                       value="${this._escAttr(v)}" placeholder="${lock ? '' : f.ph}"
                       ${lock ? 'readonly' : ''} ${f.attrs || ''}>`;
        }

        const isEtc = !!v && !(f.options || []).includes(v);
        const cur   = isEtc ? UploadSheet.BANK_ETC : v;

        return `<button type="button" class="doc-fsheet-pick js-fs-pick${v ? '' : ' is-empty'}"
                        data-key="${f.key}" data-val="${this._escAttr(cur)}">${this._escHtml(isEtc ? '기타' : (v || f.ph))}</button>
                <input type="text" class="js-fs-etc" placeholder="${this._escAttr(f.etcPh || '')}"
                       value="${this._escAttr(isEtc ? v : '')}" ${isEtc ? '' : 'hidden'}>`;
    }

    // 목록 펼치기 — 화면을 덮지 않는다. 누른 칸 바로 아래에서 시작해 화면 바닥 직전까지,
    // 넘치면 목록 안에서만 스크롤한다. 아래 입력시트는 계속 보인다.
    //   body 에 fixed 로 붙인다 — 시트(.ui-modal)가 overflow:hidden 이라 안에 넣으면 잘린다.
    //   그래서 z 는 시트 패널의 인라인 z(Layer 가 박아둔 값)보다 1 위로 잡는다.
    _openPicker(f, $btn) {
        this._closePicker();   // 목록은 한 번에 하나만

        const cur  = String($btn.attr('data-val') || '');
        const rows = (f.options || []).concat([UploadSheet.BANK_ETC]).map((o) => {
            const on = o === cur;
            return `<button type="button" class="doc-picker-item${on ? ' is-sel' : ''}" data-val="${this._escAttr(o)}">
                        <span>${this._escHtml(o === UploadSheet.BANK_ETC ? '기타' : o)}</span>
                        ${on ? '<i class="fas fa-check"></i>' : ''}
                    </button>`;
        }).join('');

        const $dd    = $(`<div class="doc-picker">${rows}</div>`).appendTo('body');
        const $panel = $btn.closest('.ui-modal');
        const r      = $btn[0].getBoundingClientRect();

        // ⚠ 단위를 맞춘다 — 글자크기 확대는 body 에 zoom 을 건다(mobile/base/_fontsize.scss).
        //   getBoundingClientRect·innerHeight 는 '확대된' px 인데, body 안에 붙는 이 목록의
        //   인라인 left/top 은 확대 전 px 로 해석돼 렌더링에서 다시 배율만큼 밀린다.
        //   → 아주크게(1.4)에서 목록이 칸보다 오른쪽·아래로 뚝 떨어졌다.
        //   배율은 '렌더폭 ÷ 레이아웃폭' 으로 직접 잰다(확대가 없으면 1 = 기존과 동일).
        const z  = $btn[0].offsetWidth ? (r.width / $btn[0].offsetWidth) : 1;
        const bt = r.top / z, bb = r.bottom / z, bl = r.left / z, bw = r.width / z;
        const vh = window.innerHeight / z;

        // 목록은 시트 밖으로 나가지 않는다 — 시트 안쪽을 경계로 잡는다(시트가 없으면 화면 기준).
        const pRect  = $panel.length ? $panel[0].getBoundingClientRect() : null;
        const pTop   = pRect ? pRect.top / z : 0;
        const pBot   = pRect ? pRect.bottom / z : vh;
        const GAP = 2;   // 칸에 붙여 놓는다 — 띄우면 남의 목록처럼 보인다
        const PAD = 8;   // 시트 가장자리에서 띄울 여백

        const below = Math.min(pBot, vh) - PAD - (bb + GAP);
        const above = (bt - GAP) - Math.max(pTop, 0) - PAD;
        const up    = below < 120 && above > below;   // 아래가 너무 좁으면 위로 펼친다
        const maxH  = Math.min(UploadSheet.PICKER_MAX_H, Math.max(60, Math.round(up ? above : below)));

        $dd.css({
            zIndex:    (parseInt($panel.css('z-index'), 10) || 1055) + 1,
            left:      Math.round(bl),
            width:     Math.round(bw),
            top:       up ? 'auto' : Math.round(bb + GAP),
            bottom:    up ? Math.round(vh - bt + GAP) : 'auto',
            maxHeight: maxH,
        });
        $btn.addClass('is-open');

        // 목록 안에서 굴리는 스크롤은 닫는 신호가 아니다 — 뒤 화면이 움직였을 때만 닫는다
        const onScroll = (e) => { if (!$dd[0].contains(e.target)) close(); };
        const close = () => {
            $dd.remove();
            $btn.removeClass('is-open');
            $(document).off('pointerdown.docpicker');
            document.removeEventListener('scroll', onScroll, true);
            window.removeEventListener('resize', close);
            this._pickerClose = null;
        };
        this._pickerClose = close;

        $dd.on('click', '.doc-picker-item', (e) => {
            this._setPickValue(f, $btn, String($(e.currentTarget).attr('data-val')));
            close();
        });

        // 고른 값이 목록 아래쪽이면 보이는 자리로 끌어온다
        const el = $dd.find('.is-sel')[0];
        if (el) el.scrollIntoView({ block: 'center' });

        // 바깥 탭·스크롤·리사이즈면 닫는다. 지금 이 클릭이 그대로 이어지지 않게 다음 틱에 건다.
        //   scroll 은 capture 로 받는다 — 시트 본문 스크롤은 document 까지 버블하지 않는다.
        setTimeout(() => {
            $(document).on('pointerdown.docpicker', (e) => {
                const $t = $(e.target);
                if ($t.closest('.doc-picker').length) return;   // 목록 안 — 고르거나 굴리는 중
                if ($t.closest($btn).length) return;            // 트리거 재탭 — click 에서 토글한다
                close();
            });
            document.addEventListener('scroll', onScroll, true);
            window.addEventListener('resize', close);
        }, 0);
    }

    // 펼쳐둔 목록 닫기 (열려 있을 때만)
    _closePicker() {
        if (this._pickerClose) this._pickerClose();
    }

    // 고른 값을 칸에 반영. '기타' 면 바로 아래 직접입력칸을 연다.
    _setPickValue(f, $btn, val) {
        const etc = val === UploadSheet.BANK_ETC;
        $btn.attr('data-val', val)
            .text(etc ? '기타' : (val || f.ph))
            .toggleClass('is-empty', !val);

        const $etc = $btn.siblings('.js-fs-etc');
        $etc.prop('hidden', !etc);
        if (etc) $etc.trigger('focus'); else $etc.val('');
    }

    // 부모 시트의 계좌칸 표시값("{은행} {계좌} {예금주}") 을 세 칸으로 되돌린다
    //   기존값은 공백 split — 첫 토큰=은행, 두번째=계좌, 나머지=예금주
    _bankValues() {
        const cur   = ($(`#${this.id}`).find('.js-big-tit-val2').val() || '').trim();
        const parts = cur.split(/\s+/).filter(Boolean);
        return {
            bank:    parts[0] || '',
            account: (parts[1] || '').replace(/\s+/g, ''), // 계좌번호는 내부 공백 제거 (레거시 데이터 방어)
            holder:  parts.slice(2).join(' '),
        };
    }

    // 시트에서 받은 값 반영. @return {boolean} 검증 통과 여부(false 면 시트를 닫지 않는다)
    //   계좌는 사진과 함께 서류행에 실려 저장되고(_doUpload), 사업자정보는 그 자리에서 g5_company 에 저장된다.
    _applyFieldSheet(kind, out) {
        const $el = $(`#${this.id}`);

        if (kind === 'bank') {
            // 은행·계좌는 공백을 걷는다 — 표시값이 "{은행} {계좌} {예금주}" 공백연결이라 토큰이 밀리면 안 된다
            const bank    = (out.bank || '').replace(/\s+/g, '');
            const account = (out.account || '').replace(/\s+/g, '');
            if (!bank || !account || !out.holder) {
                Util.swalert('은행, 계좌번호, 예금주를 모두 입력해 주세요');
                return false;
            }
            $el.find('.js-big-tit-val2').val(`${bank} ${account} ${out.holder}`);
            return true;
        }

        if (out.co_number && !this._isValidBizNo(out.co_number)) {
            Util.swalert('사업자번호 형식이 올바르지 않습니다.\n예) 123-45-67890');
            return false;
        }
        if (out.co_birth && !this._isValidDateStr(out.co_birth)) {
            Util.swalert('대표생일 형식이 올바르지 않습니다. (YYYY-MM-DD)');
            return false;
        }

        // 잠긴 칸은 readonly 라 그대로 돌아온다 — 빈값이 기존 값을 지우지 않게 기존값을 남긴다
        this.data.coNumber  = out.co_number  || this.data.coNumber  || '';
        this.data.coTitle   = out.co_title   || this.data.coTitle   || '';
        this.data.coCeoname = out.co_ceoname || this.data.coCeoname || '';
        this.data.coBirth   = out.co_birth   || this.data.coBirth   || '';

        $el.find('.js-big-tit-val2').val(this.data.coNumber);
        this._renderBizNote();
        // 사진을 안 올리고 시트를 닫아도 값은 남아야 한다 — 여기서 바로 저장한다
        this._saveCompanyBiz();
        return true;
    }

    // 추가등록 클릭
    onClickAddMore() {
        this.uploadMode = 'self';
        this._doUpload();
    }

    // 파일 다이얼로그 띄우기 (날짜/이름 검증 후)
    _doUpload() {
        this.hasDateVal = '';
        const $el = $(`#${this.id}`);

        // 장수 상한을 **가장 먼저** 본다. 뒤에 사업자정보 입력(네 칸)이 있어서, 여기서 안 막으면
        // 회원이 정보를 다 채운 뒤에 "최대 N장"으로 거절당한다.
        if (this._blockedByPageLimit()) return;

        if (this.dataCode == 9999) {
            const name = $el.find('.js-big-tit-val1').val();
            if (name.length < 2) {
                Util.swalert('서류명을 최소 2자 이상 입력해 주세요', () => {
                    setTimeout(() => $el.find('.js-big-tit-val1').focus(), 300);
                });
                return;
            }
            if (this.userGroups.indexOf(name) >= 0) {
                Util.swalert('[' + name + '] 은(는) 이미 사용중입니다.\n다른 이름을 이용해 주세요');
                return;
            }
            this.userTitle = name;
            this.docName   = name;
        }

        // 사업자등록증은 사업자정보가 먼저다 (처음 등록·추가·교체 모두) — 비어 있으면 입력 시트로 보내고,
        // 네 칸을 다 채워 확인하면 그대로 이 자리로 돌아와 사진 선택으로 넘어간다. 서버(DocService@upload)도 같은 규칙이다.
        if (this._hasBizPanel() && this._bizNeedsInput()) {
            Util.swalert('사업자정보를 먼저 입력해 주세요.\n입력을 마치면 사진을 등록할 수 있습니다.',
                () => this._openFieldSheet('biz', () => this._doUpload()));
            return;
        }

        if (!this._requiresInput()) {
            // 사업자등록증은 번호가 잠겨(입력불필요) 있어도 값을 실어보낸다 —
            // 서류행이 아직 없으면 서버(upload)가 '신규등록'으로 보고 사업자번호를 요구하기 때문이다.
            if (this._hasBizPanel()) this.hasDateVal = ($el.find('.js-big-tit-val2').val() || '').trim();
            this._openPhotoPicker();
            return;
        }

        const ss = $el.find('.js-big-tit-val2').val();
        const label = $el.find('.js-big-tit2').html();

        // 잠긴 값(사업자번호/계좌번호 기등록)은 readonly 라 유저가 고칠 수 없다.
        // 사진교체는 이미지만 바꾸는 작업이므로 검증 없이 바로 진행 —
        // 레거시 데이터(하이픈 없음·이름/메모 혼입)로 서류사진 교체가 막히는 것을 방지.
        if (this._isValueLocked()) {
            this.hasDateVal = ss;
            this._openPhotoPicker();
            return;
        }

        // 날짜 라벨(유효기간/차량검사만료일/보험만료일)은 YYYY-MM-DD 형식 + 실존 날짜 검증
        const isDate = ['유효기간', '차량검사만료일', '보험만료일'].includes(label);
        if (isDate && ss && !this._isValidDateStr(ss)) {
            Util.swalert(label + ' 형식이 맞지 않습니다.\n예) 2026-04-24');
            return;
        }

        // 사업자번호 형식 검증 (XXX-XX-XXXXX) — 값이 있을 때만 따진다
        if (label === '사업자번호' && ss && !this._isValidBizNo(ss)) {
            Util.swalert('사업자번호 형식이 올바르지 않습니다.\n예) 123-45-67890');
            return;
        }

        // 사업자등록증 — 네 칸이 다 찼는지는 위에서 이미 봤다.
        // (번호·상호·대표이름·대표생일은 각각 입력하는 즉시 g5_company 에 저장된다)
        if (this._hasBizPanel()) {
            this.hasDateVal = ss;
            this._openPhotoPicker();
            return;
        }

        if (ss.length > 8) {
            this.hasDateVal = ss;
            this._openPhotoPicker();
            return;
        }

        if (label === '사업자번호' || label === '계좌번호') {
            Util.swalert(label + '를 입력해 주세요');
        } else {
            Util.swalert(label + '을(를) 입력해 주세요');
        }
    }

    /**
     * 서류 사진 업로드 — **앱 네이티브 피커**(addPhotoHere).
     *
     * 디바이스 사진 격자(pickPhotos)는 쓰지 않는다. 격자는 BS 싱글톤을 파괴해서, 서류 시트는
     * 커밋 뒤 목록을 재조회시키는 되돌아오기 장치를 따로 들고 있어야 했다 — 같은 일을 두 경로로
     * 하게 되고 둘의 동작이 갈렸다. 격자는 **모바일 일지**(현장사진·작업문서)에만 둔다.
     *
     * 업로드 결과는 EventBus `upload:image` 로 와서 `_commitFiles` 가 순차 등록한다
     * (_initUploadListener). 시트가 살아 있으므로 진행표시도 그 자리에서 보인다.
     *
     * tag 를 'docs' 로 보낸다 — 앱의 'profile' 분기는 단건 전용 런처(pickSingleImageLauncher)라
     * max_sheet 를 무시한다. 'docs' 는 default 분기로 흡수돼(2026-05-18) limit 을
     * EXTRA_PICK_IMAGES_MAX 로 OS 에 넘긴다. (앱 MainActivity#launchImagePicker)
     *
     * 넘기는 것은 **남은 장수**다 (_remainPages) — 상한을 그대로 넘기면 상한을 넘겨 올릴 수 있다.
     * 값이 OS 피커 상한(getPickImagesMaxLimit, 통상 100)을 넘으면 OS 가 자른다 — 마스터에
     * 100 이 넘는 상한을 넣어도 그만큼은 못 고른다.
     *
     * 상한 초과 여부는 여기서 보지 않는다 — `_doUpload` 머리에서 이미 걸렀다(_blockedByPageLimit).
     * 시트가 닫히면 페이지가 서버와 재동기화하므로(BS:close → refresh) 남은 장수도 같이 새로 온다.
     */
    _openPhotoPicker() {
        // 교체(update)는 장수가 늘지 않으므로 늘 1장이다 — 이미 상한을 넘긴 서류도 갈아 끼울 수 있다
        const max_sheet = (this.uploadMode === 'update') ? 1 : this._remainPages();
        $j.F.Upload.addPhotoHere({ tag: 'docs', context: 'stampupload', max_sheet });
    }

    // 유효기간 변경 시 기존 서류에 반영
    _checkExistsUpdate(dval) {
        if (this.hasDate != 1) return;
        const ids = [];
        if (Array.isArray(this.lastShowData)) {
            this.lastShowData.forEach(line => {
                if (line && line.length > 6) {
                    const n = parseInt(line[7]);
                    if (!isNaN(n) && n > 1) ids.push(n);
                }
            });
        }
        if (ids.length === 0) return;
        const label = $(`#${this.id}`).find('.js-big-tit2').html() || '유효기간';
        $.ajax({
            type: 'POST', url: '/m/ajax/docs/expire',
            data: {
                editmembernum: this.editmembernum,
                Upload_dataCd: this.dataCode,
                editdatas: ids.toString(),
                expireddate: dval
            },
            success: () => $j.Ui.Toast.show('success', dval ? `${label} 저장됨 (${dval})` : `${label} 삭제됨`),
            error:   () => $j.Ui.Toast.show('warning', `${label} 저장 실패`)
        });
    }

    // 변경(1) / 삭제(2)
    onClickOldDataSet(act, tarnum, orderNth) {
        const self = this;
        this.editOrderNth = orderNth;
        if (act === 1) {
            if (!tarnum || tarnum < 1) {
                Util.swalert('수정할 자료 번호 오류');
                return;
            }
            this.showNum = tarnum;
            this.uploadMode = 'update';
            this._doUpload();
            return;
        }

        Util.swalert(['이 자료를 삭제 할까요?', 'question'], () => {
            $.ajax({
                type: 'DELETE', url: `/m/ajax/docs/${tarnum}`, dataType: 'json',
                data: { data_code: self.dataCode, editmembernum: self.editmembernum },
                success: (data) => {
                    if (self.remainPages != null) self.remainPages += 1;
                    self.lastShowData = data.redata || [];
                    self._renderBigImgOnlist(self.lastShowData);
                    self._hideAddMoreButton();
                    $j.Common.EventBus.trigger('docs:changed', {
                        dataCode: self.dataCode,
                        data: self.lastShowData,
                        dbtype: 'delete'
                    });
                }
            });
        }, ['네', '아니오']);
    }

    // 이미지 편집 (ImageEditor)
    onClickEditPhoto(num, imgId) {
        // 지워진 g5_file 을 가리키는 서류가 있다 — 번호만 있고 이미지가 없으면 열지 않는다
        if (!imgId) { $j.Ui.Toast.show('편집할 이미지가 없습니다.', { type: 'warning' }); return; }
        G5['currentEditDocId'] = num;
        $j.F.ImageEditor.open(imgId);
    }

    /**
     * 업로드된 파일들을 서류로 등록한다.
     *
     * 앱은 파일서버 업로드까지만 하고 배열을 돌려준다 — 서류 등록(2단계)은 여기서 한다.
     * **받은 순서 그대로** 순차 등록하면 `g5_document_pages.page_no` 오름차순이 곧 페이지 순서가
     * 되므로 별도 정렬 컬럼이 필요 없다. (앱은 사용자가 고른 순서로 배열을 돌려준다)
     *
     * ⚠ 순차 실행이 규약이다. 병렬로 쏘면 num 이 뒤섞여 페이지 순서가 깨진다.
     */
    _commitFiles(files) {
        const list = (files || []).filter(f => f && f.id);
        if (!list.length) return Promise.resolve();
        return this._registerSeq(list);
    }

    /** 확정 순서대로 한 장씩 등록. 앞 건이 끝나야 다음 건을 보낸다. */
    _registerSeq(files) {
        const self = this;
        const total = files.length;
        let i = 0;

        const step = () => {
            if (i >= total) { self._setSeqProgress(0, 0); return Promise.resolve(); }
            const f = files[i++];
            self._setSeqProgress(i, total);
            return Promise.resolve(self._onUploadComplete(Util.getImageUrl(f.path, f.host), f.id))
                .then(step);
        };
        return step();
    }

    /** 순차 등록 진행 표시 (total 이 0 이면 감춘다) */
    _setSeqProgress(cur, total) {
        const $el = $(`#${this.id}`);
        let $bar = $el.find('.doc-seq-progress');
        if (!total) { $bar.remove(); return; }
        if (!$bar.length) {
            $bar = $('<div class="doc-seq-progress"></div>').prependTo($el);
        }
        $bar.text(`등록 중 ${cur} / ${total}`);
    }

    // 업로드 완료 후 서버 저장 + 갱신
    _onUploadComplete(fileName, relId) {
        const self = this;
        if (!fileName) return;
        const $el = $(`#${this.id}`);

        if (this.uploadMode === 'update') {
            const $existing = $el.find(`img[data-rs-num="${this.showNum}"]`);
            if ($existing.length) $existing.attr('src', fileName + '?stmp=' + Date.now());
        } else if (this.showNum === 99999) {
            this.userGroups.push(this.docName);
        }

        $el.find('.js-big-tit-val1').prop('readonly', true);
        if (this.uploadMode === 'self') this.showNum = 0;

        const sendData = {
            data_code: this.dataCode,
            hadexdate: this.hasDate,
            exdateval: this.hasDateVal,
            uploadfilename: fileName,
            file_id: relId || '',
            data_codesub: this.dataSubCode,
            Upload_showNum: this.showNum,
            editmembernum: this.editmembernum,
            docname: this.docName,
            carnum: this.carnum
        };

        // 사업자등록증이면 사업자정보(상호·대표이름·대표생일)도 같이 보낸다.
        // change 저장과 겹쳐도 서버는 같은 값을 다시 쓸 뿐이라 안전하다 —
        // 칸을 채우자마자 촬영버튼을 눌러 change 가 안 뜨는 경우를 여기서 받는다.
        if (this._hasBizPanel()) {
            const biz = this._bizValues();
            sendData.co_title   = biz.co_title;
            sendData.co_ceoname = biz.co_ceoname;
            sendData.co_birth   = biz.co_birth;
        }

        const requestedCd = this.dataCode;

        // 1단계 업로드(/api/upload) 에서 켜진 로딩을 그대로 유지하고,
        // 2단계 등록(/m/ajax/docs/upload) 완료 시 해제 (안드로이드 경로에선 여기서 처음 노출)
        $j.Common.Util.loadingOverlay(true);
        return $.ajax({
            type: 'POST', url: '/m/ajax/docs/upload', dataType: 'json', data: sendData,
            success: (data) => {
                // 서버가 거절해도(rel_id 누락·검증실패) 여기서 result 를 안 보면 빈 목록으로
                // 조용히 렌더돼 회원은 등록된 줄 안다. CS 도 "올렸다는데 없다"만 듣게 된다.
                if (!data || data.result === false) {
                    Util.swalert((data && data.error) || '서류 등록에 실패했습니다.\n잠시 후 다시 시도해 주세요.');
                    return;
                }

                const newdata = data.newdata || [];

                // 남은 장수를 시트가 이어서 깎는다 — 서버가 준 값은 시트를 열 때의 값이고,
                // 여러 장을 연속 등록하는 동안 다시 물어볼 자리가 없다.
                if (self.uploadMode !== 'update' && self.remainPages != null) {
                    self.remainPages = Math.max(0, self.remainPages - 1);
                }

                if (self.uploadMode === 'self' || self.editOrderNth === 1) {
                    const rsno = parseInt(data.lastEditNo);
                    if (self.targetObj) self.targetObj.setAttribute('data_rsno', isNaN(rsno) ? -1 : rsno);

                    if (requestedCd == 9999 && newdata.length > 0) {
                        self.dataCode = parseInt(data.data_code);
                        $j.Common.EventBus.trigger('docs:new-custom', {
                            dataCode: self.dataCode,
                            docName: self.docName,
                            intype: self.editIntype,
                            data: newdata
                        });
                    }

                    self.lastShowData = newdata;
                    if (data.dbtype === 'new') self._renderBigImgOnlist(newdata);
                }

                self._hideAddMoreButton();

                $j.Common.EventBus.trigger('docs:changed', {
                    dataCode: self.dataCode,
                    data: newdata,
                    dbtype: data.dbtype
                });
            },
            error: () => Util.swalert('서류 등록에 실패했습니다.\n통신 상태를 확인한 뒤 다시 시도해 주세요.'),
            complete: () => $j.Common.Util.loadingOverlay(false),
        });

        // $j.F.App.on_file_upload_end();
    }

    _renderBigImgOnlist(data) {
        const $el = $(`#${this.id}`);
        const $box = $el.find('.js-big-img-onlist');

        if (!data || data.length === 0) {
            this.firstDataHas = 0;
            $box.html('');
            return;
        }
        this.firstDataHas = data.length;

        let html = '';
        for (let i = 0; i < data.length; i++) {
            const d = data[i];
            const num = d[7];
            const imgId = d[10];
            if (this.editOrderNth === 0 && i === 0 && this.targetObj) {
                this.targetObj.style.backgroundImage = `url('${d[1]}')`;
            }
            html += `
                <div class="doc-sheet-photo">
                    <img class="js-zoom-img" src="${d[1]}" data-rs-num="${num}">
                    <button type="button" class="doc-sheet-edit js-edit-photo"
                            data-num="${num}" data-img-id="${imgId}" aria-label="사진편집">
                        <i class="fa-solid fa-pen-to-square"></i>
                    </button>
                </div>
                <div class="doc-sheet-acts">
                    <button type="button" class="doc-sheet-btn js-change-photo" data-num="${num}" data-idx="${i}">
                        <i class="fa-solid fa-arrows-rotate"></i>사진교체
                    </button>
                    <button type="button" class="doc-sheet-btn is-danger js-delete-photo" data-num="${num}" data-idx="${i}">
                        <i class="fa-solid fa-trash-can"></i>삭제
                    </button>
                </div>`;
        }
        $box.html(html);
    }

    _escHtml(s) { return String(s == null ? '' : s).replace(/[&<>"']/g, c => ({'&':'&amp;','<':'&lt;','>':'&gt;','"':'&quot;',"'":'&#39;'}[c])); }
    _escAttr(s) { return this._escHtml(s); }

    // 기본 서류(사업자등록증/통장사본) 은 2개 이상이면 추가등록 버튼 숨김
    _hideAddMoreButton() {
        const $addMore = $(`#${this.id}`).find('.js-add-more');
        if ($.inArray(this.docName, ['사업자등록증', '통장사본']) === -1) {
            $addMore.show();
            return;
        }
        const cnt = $(`#${this.id}`).find('img[data-rs-num]').length;
        if (cnt >= 2) $addMore.hide();
        else          $addMore.show();
    }
}
